import { useEffect, useRef, useState } from 'react'
import { useParams, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { type SupportedLocale } from '../i18n'
import { isSupported, getCachedLocale, setCachedLocale, browserLocale } from '../utils/localeCache'

const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  US: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en', ZA: 'en', SG: 'en', IN: 'en',
  FR: 'fr', MC: 'fr', BE: 'fr',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
}

export function LocaleLayout() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && isSupported(lang)) {
      i18n.changeLanguage(lang)
      document.documentElement.lang = lang
      setCachedLocale(lang)
    }
  }, [lang, i18n])

  if (!lang || !isSupported(lang)) {
    return <Navigate to={`/en/`} replace />
  }

  return <Outlet />
}

export function LocaleRedirect() {
  const cached = useRef(getCachedLocale())
  const [locale, setLocale] = useState<SupportedLocale | null>(cached.current)

  useEffect(() => {
    if (locale !== null) return
    const controller = new AbortController()
    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then(r => r.json())
      .then((data: { country_code?: string }) => {
        const detected = COUNTRY_TO_LOCALE[data.country_code ?? ''] ?? 'en'
        setCachedLocale(detected)
        setLocale(detected)
      })
      .catch(() => {
        const fallback = browserLocale()
        setCachedLocale(fallback)
        setLocale(fallback)
      })
    return () => controller.abort()
  }, [locale])

  if (locale === null) return null
  return <Navigate to={`/${locale}/`} replace />
}

export function LegacyRedirect() {
  const location = useLocation()
  const cached = useRef(getCachedLocale())
  const [locale, setLocale] = useState<SupportedLocale | null>(cached.current)

  useEffect(() => {
    if (locale !== null) return
    const controller = new AbortController()
    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then(r => r.json())
      .then((data: { country_code?: string }) => {
        const detected = COUNTRY_TO_LOCALE[data.country_code ?? ''] ?? 'en'
        setCachedLocale(detected)
        setLocale(detected)
      })
      .catch(() => {
        const fallback = browserLocale()
        setCachedLocale(fallback)
        setLocale(fallback)
      })
    return () => controller.abort()
  }, [locale])

  if (locale === null) return null
  return <Navigate to={`/${locale}${location.pathname}${location.search}${location.hash}`} replace />
}
